export interface VerificationOutcome {
  passed: boolean;
  details: string[];
}

export interface ImageVerificationInput {
  expectedWidth: number;
  expectedHeight: number;
  outputWidth: number;
  outputHeight: number;
  /** 声明为无损路径时必须做像素抽样比对。 */
  expectLosslessPixels: boolean;
  sourceSample?: Uint8ClampedArray | null;
  outputSample?: Uint8ClampedArray | null;
  /** 允许的单通道最大差值，默认 0（无损路径要求逐点一致）。 */
  tolerance?: number;
}

export function maxChannelDifference(a: Uint8ClampedArray, b: Uint8ClampedArray): number {
  const length = Math.min(a.length, b.length);
  let max = 0;
  for (let i = 0; i < length; i += 1) {
    const diff = Math.abs(a[i] - b[i]);
    if (diff > max) max = diff;
  }
  return max;
}

/**
 * 图片输出校验：
 * 1. 尺寸必须与预期一致；
 * 2. 声称无损时必须抽样比对像素——抽样不可用时如实降级为结构校验，不做虚假承诺。
 */
export function verifyImageOutput(input: ImageVerificationInput): VerificationOutcome {
  const details: string[] = [];
  if (input.expectedWidth !== input.outputWidth || input.expectedHeight !== input.outputHeight) {
    return {
      passed: false,
      details: [
        `输出尺寸 ${input.outputWidth}×${input.outputHeight} 与预期 ${input.expectedWidth}×${input.expectedHeight} 不一致`,
      ],
    };
  }

  if (!input.expectLosslessPixels) {
    details.push('产物可正常解码，尺寸一致');
    return { passed: true, details };
  }

  const sourceSample = input.sourceSample;
  const outputSample = input.outputSample;
  if (!sourceSample || !outputSample || sourceSample.length !== outputSample.length) {
    details.push('无法抽样比对像素，本次仅完成解码与尺寸校验');
    return { passed: true, details };
  }

  const tolerance = input.tolerance ?? 0;
  const difference = maxChannelDifference(sourceSample, outputSample);
  if (difference > tolerance) {
    return {
      passed: false,
      details: [`无损路径像素比对失败，单通道最大差值 ${difference}（容差 ${tolerance}）`],
    };
  }
  details.push(`像素抽样一致（单通道最大差值 ${difference}）`);
  return { passed: true, details };
}

export interface VerificationAssertion {
  label: string;
  ok: boolean;
  /**
   * 提示性断言：不通过只记录、**不阻断下载**。
   *
   * 用于启发式判据 —— 例如「产物里有没有不配对的三反引号」。这类判据的输入串来自
   * 用户文档本身（我们的转换器并不会凭空生成代码围栏），因此「不通过」既可能是
   * 真的截断，也可能只是文档里恰好写了奇数个 ```；把它当硬门禁会误伤好产物
   * （同类误伤已经在「残留 HTML 标签」上真实发生过）。客观判据（OOXML 残留、
   * 产物无法解析）才阻断。
   */
  advisory?: boolean;
}

/** 文档类产物校验：解析可读 + **非提示性**断言全部成立才算通过。 */
export function verifyDocumentOutput(input: { parsed: unknown; assertions: VerificationAssertion[] }): VerificationOutcome {
  if (input.parsed === undefined || input.parsed === null) {
    return { passed: false, details: ['产物无法被目标格式解析器读取'] };
  }

  const failed = input.assertions.filter((item) => !item.ok && !item.advisory);
  const advisories = input.assertions.filter((item) => !item.ok && item.advisory);

  if (failed.length > 0) {
    return {
      passed: false,
      details: [
        ...failed.map((item) => `结构校验未通过：${item.label}`),
        ...advisories.map((item) => `另有提示（不阻断下载）：${item.label}`),
      ],
    };
  }

  return {
    passed: true,
    details: [
      '产物可被目标解析器读取',
      ...input.assertions.filter((item) => item.ok).map((item) => `结构校验通过：${item.label}`),
      ...advisories.map((item) => `提示（不阻断下载）：${item.label}`),
    ],
  };
}

/** 代码围栏数量是否为奇数（启发式：见 VerificationAssertion.advisory 的说明）。 */
export function hasUnbalancedFence(text: string): boolean {
  return (text.match(/```/g)?.length ?? 0) % 2 !== 0;
}

const HTML_TAG_NAMES =
  'p|div|span|section|article|table|thead|tbody|tfoot|tr|td|th|caption|ul|ol|li|h[1-6]|br|hr|blockquote|pre|code|strong|em|b|i|u|s|img|figure|figcaption|a|sup|sub|dl|dt|dd';

/** 行首出现的块级开/闭标签：HTML 残留的典型形态 */
const HTML_BLOCK_TAG = new RegExp(`(^|\\n)\\s*</?(?:${HTML_TAG_NAMES})\\b[^>]*>`, 'i');
/** 任意位置的闭标签：`</p>` 这类写法不可能出现在正常正文里 */
const HTML_CLOSING_TAG = new RegExp(`</(?:${HTML_TAG_NAMES})\\s*>`, 'i');

/**
 * 判定「产物疑似残留 HTML」，并给出匹配到的片段（便于用户与运维判断）。
 *
 * 不能用 `/‹[a-z][^>]*›/` 这种「尖括号后跟字母就算」的松判据 —— 正文里合法出现
 * `<https://example.com>`、`<your-name>`、`<待替换>` 都会被误伤，而用户对此毫无办法：
 * 一次误判就会把完好的产物拦下（线上真实发生过：一份含网址的合同转 Markdown 被拦）。
 *
 * 判据改为「块级标签（行首）或闭标签」：mammoth 之类的库残留 HTML 时，产物里必然
 * 出现 `</p>`、`<div ...>` 这类结构；而正文里的尖括号通常是内联的 `<https://…>`，
 * 标签名也不会恰好是这些块级名。
 */
export function looksLikeHtmlResidue(text: string): { residue: boolean; sample?: string } {
  const match = HTML_BLOCK_TAG.exec(text) ?? HTML_CLOSING_TAG.exec(text);
  if (!match) return { residue: false };
  const sample = match[0].replace(/\s+/g, ' ').trim().slice(0, 60);
  return { residue: true, sample };
}
